import React from 'react';
import type { Tone } from '../../domain/types';
import { getToneColors } from './badgeToneColors';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  label?: React.ReactNode;
  tone?: Tone;
  dot?: boolean;
  icon?: React.ReactNode;
  size?: 'sm' | 'md';
  variant?: 'soft' | 'solid' | 'outline';
  interactive?: boolean;
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  label,
  tone = 'flat',
  dot = false,
  icon,
  size = 'md',
  variant = 'soft',
  interactive = false,
  className = '',
  style,
  ...props
}) => {
  const colors = getToneColors(tone, variant);
  const isSmall = size === 'sm';

  const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: isSmall ? '4px' : '6px',
    padding: isSmall ? '2px 7px' : '3px 9px',
    borderRadius: '7px',
    backgroundColor: colors.bg,
    color: colors.text,
    border: 'none',
    fontSize: isSmall ? '10.5px' : '11px',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    cursor: interactive ? 'pointer' : 'default',
    transition: 'all 0.15s ease',
    ...style
  };

  const dotStyle: React.CSSProperties = {
    width: isSmall ? '4px' : '6px',
    height: isSmall ? '4px' : '6px',
    borderRadius: '50%',
    backgroundColor: colors.dot,
    flexShrink: 0
  };

  return (
    <span
      className={`z-badge z-badge-${tone} z-badge-${size} z-badge-${variant} ${className}`}
      role={interactive ? 'button' : props.role}
      tabIndex={interactive ? (props.tabIndex ?? 0) : props.tabIndex}
      style={badgeStyle}
      {...props}
    >
      {dot && <span className="z-badge-dot" style={dotStyle} aria-hidden="true" />}
      {icon && <span className="z-badge-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>}
      {label ?? children}
    </span>
  );
};

